import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { toast } from 'react-toastify';
import {
  Button,
  Flex,
  FormGroup,
  FormControl,
  FormControlText,
  Input,
  InputText,
  designTokens,
  InputCheckBox
} from '@itandi/itandi-bb-ui';
import { useSWRConfig } from 'swr';
import type { FC } from 'react';
import type { VendorCompanyUpdateSelfParams } from '~/models/VendorCompany';
import { vendorCompanyEditValidationSchema } from '~/models/VendorCompany';
import { updateSelf } from '~/utils/api/vendors/VendorCompany';
import { isSuccess } from '~/utils/api/api_handler';
import type { VendorSession } from '~/models/VendorSession';
import { generatePath } from '~/utils/generatePath';
import { showNewFeatureForLocal } from '~/utils/FeatureFlagUtil';

type Props = {
  session: VendorSession;
};

export const AccountSettingForm: FC<Props> = ({ session }) => {
  const { mutate: globalMutate } = useSWRConfig();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isDirty, isSubmitting }
  } = useForm<VendorCompanyUpdateSelfParams>({
    resolver: yupResolver(vendorCompanyEditValidationSchema),
    defaultValues: {
      name: session.vendorCompany.name,
      ceoLastName: session.vendorCompany.ceoLastName ?? '',
      ceoFirstName: session.vendorCompany.ceoFirstName ?? '',
      tel: session.vendorCompany.tel,
      loginEmail: session.vendorCompany.loginEmail,
      fax: session.vendorCompany.fax,
      postalCode: session.vendorCompany.postalCode ?? '',
      address: session.vendorCompany.address ?? '',
      teamName: session.vendorCompany.teamName ?? '',
      staffName: session.vendorCompany.staffName ?? '',
      defaultCautionaryNoteToCompany:
        session.vendorCompany.defaultCautionaryNoteToCompany ?? '',
      dealVendorNoteTemplate:
        session.vendorCompany.dealVendorNoteTemplate ?? '',
      estimationNoteTemplate:
        session.vendorCompany.estimationNoteTemplate ?? '',
      useLineNotification: session.vendorCompany.useLineNotification
    },
    mode: 'onBlur'
  });

  const onSubmit = async (
    submitValue: VendorCompanyUpdateSelfParams
  ): Promise<void> => {
    const result = await updateSelf(submitValue);
    if (isSuccess(result)) {
      await globalMutate(generatePath('/vendors/sessions'));
      toast.success('変更を保存しました。');
    } else {
      toast.error(result.message ?? 'エラーが発生しました。');
    }
  };

  return (
    <form noValidate onSubmit={handleSubmit(onSubmit)}>
      <FormGroup maxW="32rem" variant="Vertical">
        <FormControl chip="必須" isInvalid={!!errors.name} label="アカウント名">
          <Input {...register('name')} placeholder="例）イタンジ株式会社" />
          <FormControlText error={errors.name?.message} />
        </FormControl>
        <FormControl
          chip="必須"
          isInvalid={!!(errors.ceoLastName || errors.ceoFirstName)}
          label="代表者氏名"
        >
          <Flex gap={designTokens['spacing/xs']}>
            <Input {...register('ceoLastName')} placeholder="例）内装" />
            <Input {...register('ceoFirstName')} placeholder="例）太郎" />
          </Flex>
          <FormControlText
            error={errors.ceoLastName?.message || errors.ceoFirstName?.message}
          />
        </FormControl>
        <FormControl label="代表電話番号">
          <Input {...register('tel')} placeholder="例）03-1234-5678" />
        </FormControl>
        <FormControl label="FAX番号">
          <Input {...register('fax')} placeholder="例）03-1234-5678" />
        </FormControl>
        <FormControl
          chip="必須"
          isInvalid={!!errors.loginEmail}
          label="メールアドレス"
        >
          <Input
            {...register('loginEmail')}
            placeholder="例）itandi.tarou@example.com"
            type="email"
          />
          <FormControlText error={errors.loginEmail?.message} />
        </FormControl>
        <FormControl
          chip="必須"
          isInvalid={!!errors.postalCode}
          label="郵便番号"
        >
          <Input {...register('postalCode')} placeholder="例）1234567" />
          <FormControlText error={errors.postalCode?.message} />
        </FormControl>
        <FormControl chip="必須" isInvalid={!!errors.address} label="住所">
          <Input
            {...register('address')}
            placeholder="例）東京都港区六本木3丁目2-1 六本木グランドタワー42階"
          />
          <FormControlText error={errors.address?.message} />
        </FormControl>
        <FormControl label="担当チーム名">
          <Input {...register('teamName')} placeholder="例）イタンジ株式会社" />
          <FormControlText help="メールの文面等で利用されます" />
        </FormControl>
        <FormControl label="担当者の呼び名">
          <Input
            {...register('staffName')}
            placeholder="例）イタンジ株式会社"
          />
        </FormControl>
        {showNewFeatureForLocal() && (
          <FormControl label="見積り全体の備考テンプレート">
            <InputText rows={8} {...register('estimationNoteTemplate')} />
            <FormControlText
              error={errors.estimationNoteTemplate?.message}
              help="ここで入力した文章が見積り全体の備考にあらかじめ入力されます"
            />
          </FormControl>
        )}
        <FormControl label="管理会社様への注意事項">
          <InputText {...register('defaultCautionaryNoteToCompany')} rows={8} />
          <FormControlText
            error={errors.defaultCautionaryNoteToCompany?.message}
            help="改行は半角スペース2つ、URLは[リンク名](URL)で記載可能です"
          />
        </FormControl>
        <FormControl label="共有案件メモテンプレート">
          <InputText {...register('dealVendorNoteTemplate')} rows={8} />
          <FormControlText
            error={errors.dealVendorNoteTemplate?.message}
            help={[
              '改行は半角スペース2つ、URLは[リンク名](URL)で記載可能です',
              'ここで入力した文章が共有案件メモにあらかじめ入力されます'
            ]}
          />
        </FormControl>
        {session.admin && (
          <FormControl label="通知設定">
            <InputCheckBox
              {...register('useLineNotification')}
              checked={watch('useLineNotification')}
              label="LINE通知設定を利用する"
            />
          </FormControl>
        )}
      </FormGroup>
      <Button
        disabled={!isDirty}
        loading={isSubmitting}
        my={designTokens['spacing/m']}
        type="submit"
        variant="primary"
      >
        変更を保存
      </Button>
    </form>
  );
};
